import { supabase } from '@/lib/supabase';
import { MessageType } from '@/types/assistant';
import { v4 as uuidv4 } from 'uuid';
import { logger } from '@/utils/production-logger';
import { 
  DEFAULT_MESSAGE_VALIDATION, 
  ValidationResult, 
  ValidationRule,
  MessageValidationConfig 
} from '@/config/message-validation-config';

export class MessageService {
  // Use configurable validation rules
  private static validationConfig: MessageValidationConfig = DEFAULT_MESSAGE_VALIDATION;

  /**
   * Update validation configuration
   */
  static updateValidationConfig(config: Partial<MessageValidationConfig>): void {
    this.validationConfig = { ...this.validationConfig, ...config };
  }

  /**
   * Create a new user message with proper UUID
   */
  static createUserMessage(
    content: string,
    sessionId: string,
    userId: string
  ): MessageType {
    return {
      id: uuidv4(),
      content,
      role: 'user',
      timestamp: new Date(),
      session_id: sessionId,
      user_id: userId,
      suggestions: [],
      feedback: null,
      is_streaming: false, // User messages are never streaming
    };
  }

  /**
   * Create a new AI message with proper UUID
   */
  static createAiMessage(
    content: string,
    sessionId: string,
    userId: string,
    suggestions: string[] = []
  ): MessageType {
    return {
      id: uuidv4(),
      content,
      role: 'assistant',
      timestamp: new Date(),
      session_id: sessionId,
      user_id: userId,
      suggestions,
      feedback: null,
      is_streaming: false, // AI messages are complete when created
    };
  }

  /**
   * Check if message already exists in database
   */
  static async messageExists(messageId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('assistant_messages')
        .select('id')
        .eq('id', messageId)
        .limit(1);

      if (error) {
        logger.warn('Error checking message existence', {
          error: error.message,
          messageId,
          component: 'MessageService'
        });
        return false;
      }

      return data && data.length > 0;
    } catch (error) {
      logger.warn('Unexpected error checking message existence', {
        error: error instanceof Error ? error.message : String(error),
        messageId,
        component: 'MessageService'
      });
      return false;
    }
  }

  /**
   * STEP 2: Enhanced save message with proper streaming state lifecycle
   */
  static async saveMessage(message: MessageType): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('[MESSAGE SERVICE FINAL FIX] 💾 Saving message with proper streaming lifecycle:', {
        messageId: message.id,
        role: message.role,
        isStreaming: message.is_streaming,
        hasContent: !!message.content,
        hasStreamContent: !!message.stream_content,
        hasStreamTimestamp: !!message.stream_timestamp,
        contentLength: message.content?.length || 0,
        streamContentLength: message.stream_content?.length || 0
      });

      // Validate message before saving
      if (!message.id || message.id.startsWith('temp-')) {
        const error = 'Invalid message ID: cannot save temporary message';
        logger.error('Message save validation failed', {
          messageId: message.id,
          error,
          component: 'MessageService',
          metadata: { messageRole: message.role }
        });
        return { success: false, error };
      }

      if (!message.session_id || !message.user_id) {
        const error = 'Missing required fields: session_id or user_id';
        logger.error('Message save validation failed', {
          messageId: message.id,
          error,
          component: 'MessageService'
        });
        return { success: false, error };
      }

      // STEP 2: Skip saving streaming messages (only save when completed)
      if (message.is_streaming) {
        console.log('[MESSAGE SERVICE FINAL FIX] ⏭️ Skipping save for streaming message (will save when completed):', {
          messageId: message.id,
          isStreaming: message.is_streaming
        });
        return { success: true }; // Return success but don't actually save
      }

      // Check if message already exists to prevent duplicates
      const exists = await this.messageExists(message.id);
      if (exists) {
        console.log('[MESSAGE SERVICE FINAL FIX] 📝 Updating existing message (completion):', {
          messageId: message.id
        });
        return await this.updateExistingMessage(message);
      }

      // STEP 2: Prepare data with proper completed state preservation
      const saveData = {
        id: message.id,
        session_id: message.session_id,
        user_id: message.user_id,
        role: message.role,
        content: message.content || '',
        created_at: message.timestamp.toISOString(),
        suggestions: message.suggestions || [],
        // STEP 2: Preserve streaming state as-is (should be false for completed messages)
        is_streaming: Boolean(message.is_streaming),
        stream_content: message.stream_content || message.content || '',
        stream_timestamp: message.stream_timestamp ? message.stream_timestamp.toISOString() : new Date().toISOString(),
        model: message.model || null,
        feedback: message.feedback || null,
        metadata: message.metadata || {}
      };

      console.log('[MESSAGE SERVICE FINAL FIX] 💾 Saving completed message with proper state:', {
        messageId: saveData.id,
        isStreaming: saveData.is_streaming,
        hasStreamContent: !!saveData.stream_content,
        hasStreamTimestamp: !!saveData.stream_timestamp,
        streamContentLength: saveData.stream_content?.length || 0,
        contentLength: saveData.content?.length || 0
      });

      const { error, data } = await supabase
        .from('assistant_messages')
        .insert(saveData)
        .select();

      if (error) {
        // Handle specific database errors
        if (error.code === '23505') { // Primary key violation
          logger.debug('Message already exists (primary key violation), treating as success', {
            messageId: message.id,
            component: 'MessageService'
          });
          return { success: true };
        }

        console.error('[MESSAGE SERVICE FINAL FIX] ❌ Database error:', {
          error: error.message,
          messageId: message.id,
          errorCode: error.code
        });

        logger.error('Database error saving message', {
          error: error.message,
          messageId: message.id,
          component: 'MessageService',
          metadata: { 
            errorCode: error.code || 'unknown',
            errorDetails: error.details || 'no details'
          }
        });
        return { success: false, error: `Database error: ${error.message}` };
      }

      console.log('[MESSAGE SERVICE FINAL FIX] ✅ Message saved successfully with proper streaming state:', {
        messageId: message.id,
        userId: message.user_id,
        isStreaming: saveData.is_streaming,
        savedStreamData: !!saveData.stream_content
      });

      logger.debug('Message saved successfully', {
        messageId: message.id,
        userId: message.user_id,
        component: 'MessageService',
        metadata: { insertedData: data ? 'success' : 'no_data' }
      });

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('[MESSAGE SERVICE FINAL FIX] ❌ Unexpected error:', {
        error: errorMessage,
        messageId: message.id
      });
      logger.error('Unexpected error saving message', {
        error: errorMessage,
        messageId: message.id,
        component: 'MessageService'
      });
      return { success: false, error: `Unexpected error: ${errorMessage}` };
    }
  }

  /**
   * STEP 2: Update existing message for streaming completion
   */
  private static async updateExistingMessage(message: MessageType): Promise<{ success: boolean; error?: string }> {
    try {
      const updateData = {
        content: message.content || '',
        suggestions: message.suggestions || [],
        is_streaming: false, // STEP 2: Mark as completed
        stream_content: message.stream_content || message.content || '',
        stream_timestamp: message.stream_timestamp ? message.stream_timestamp.toISOString() : new Date().toISOString(),
        model: message.model || null,
        feedback: message.feedback || null,
        metadata: message.metadata || {},
        updated_at: new Date().toISOString()
      };

      console.log('[MESSAGE SERVICE FINAL FIX] 📝 Updating existing message to completed state:', {
        messageId: message.id,
        wasStreaming: true,
        nowCompleted: !updateData.is_streaming,
        streamContentLength: updateData.stream_content?.length || 0
      });

      const { error } = await supabase
        .from('assistant_messages')
        .update(updateData)
        .eq('id', message.id);

      if (error) {
        console.error('[MESSAGE SERVICE FINAL FIX] ❌ Error updating message:', {
          error: error.message,
          messageId: message.id
        });
        return { success: false, error: error.message };
      }

      console.log('[MESSAGE SERVICE FINAL FIX] ✅ Message updated successfully to completed state:', {
        messageId: message.id
      });

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('[MESSAGE SERVICE FINAL FIX] ❌ Error in updateExistingMessage:', {
        error: errorMessage,
        messageId: message.id
      });
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Verify database connection and table structure
   */
  static async verifyDatabaseConnection(): Promise<{ success: boolean; error?: string }> {
    try {
      logger.debug('Verifying database connection', {
        component: 'MessageService'
      });

      const { error } = await supabase
        .from('assistant_messages')
        .select('id')
        .limit(1);

      if (error) {
        logger.error('Database connection failed', {
          error: error.message,
          component: 'MessageService'
        });
        return { success: false, error: error.message };
      }

      logger.debug('Database connection verified', {
        component: 'MessageService'
      });

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error('Database verification failed', {
        error: errorMessage,
        component: 'MessageService'
      });
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Build conversation history for AI requests
   */
  static buildConversationHistory(
    messages: MessageType[],
    newUserMessage: string,
    maxMessages: number = 10
  ): Array<{ role: string; content: string }> {
    const conversationHistory = messages
      .slice(-maxMessages)
      .map(msg => ({
        role: msg.role,
        content: msg.content
      }));

    conversationHistory.push({
      role: 'user',
      content: newUserMessage
    });

    return conversationHistory;
  }

  /**
   * Enhanced message validation with configurable rules
   */
  static validateMessage(content: string): ValidationResult {
    const warnings: string[] = [];
    
    // Basic validation
    if (!content || content.trim().length === 0) {
      return { 
        isValid: false, 
        error: 'Message content cannot be empty',
        severity: 'error'
      };
    }

    if (content.length > this.validationConfig.maxLength) {
      return { 
        isValid: false, 
        error: `Message content is too long (max ${this.validationConfig.maxLength} characters)`,
        severity: 'error'
      };
    }

    if (content.length < this.validationConfig.minLength) {
      return { 
        isValid: false, 
        error: `Message content is too short (min ${this.validationConfig.minLength} characters)`,
        severity: 'error'
      };
    }

    // Check forbidden patterns
    for (const pattern of this.validationConfig.forbiddenPatterns) {
      if (pattern.test(content)) {
        return { 
          isValid: false, 
          error: 'Message content contains forbidden patterns',
          severity: 'error'
        };
      }
    }

    // Check custom rules
    for (const rule of this.validationConfig.customRules) {
      if (!rule.validate(content)) {
        if (rule.severity === 'error') {
          return { 
            isValid: false, 
            error: rule.errorMessage,
            severity: 'error'
          };
        } else {
          warnings.push(rule.errorMessage);
        }
      }
    }

    return { 
      isValid: true, 
      warnings: warnings.length > 0 ? warnings : undefined,
      severity: warnings.length > 0 ? 'warning' : 'none'
    };
  }

  /**
   * Get current validation configuration
   */
  static getValidationConfig(): MessageValidationConfig {
    return { ...this.validationConfig };
  }
}
